// #region Imports
import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ViewChildren, QueryList, ElementRef, Input, ChangeDetectorRef, HostListener } from '@angular/core';
import html2canvas from 'html2canvas';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MsalService } from '@azure/msal-angular';
import { of } from 'rxjs';
import { ChatbotService } from './../../common/services/chatbot.service';
import { InsightsModel, results } from 'src/app/common/models/common.model';
import { TableOuputComponent } from 'src/app/common/shared/table-ouput/table-ouput.component';
import { CrosstabService } from '../../common/services/crosstab.service';
import { LoaderService } from 'src/app/common/services/loader.service';
import { ExportDownloadServices } from 'src/app/common/services/exports-downloads.service';
import { SharedChatService } from 'src/app/common/services/shared-chat.service';
import { Subscription, switchMap, take, tap, firstValueFrom, from, catchError, throwError, forkJoin, map, Observable, shareReplay, finalize, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DataObject, checkboxSelection, checkboxSelectionWithParent, OutputDataObject, CrossTabOutputItem, StackChart, LineChart, GroupBarChart} from 'src/app/common/models/crosstab.model';
import { Feature } from 'src/app/common/models/usermanagement.model';
import { InsightspopupService } from 'src/app/common/services/insightspopup.service';
import { fomData, StudyData, DiagnosticData, ChatListDocument, ChatHistoryDocument, ChatAnswerDocument, UserIdentityDocument, SaveChatResponseRequest } from 'src/app/common/models/chatbotexpand.model';
import { UserProjectManagementService } from 'src/app/common/services/user-project-management.service';
import * as XLSX from 'xlsx';
import { COMMON_CONSTANTS } from 'src/app/common/constants/common-constants';
import * as _ from 'lodash';
import { CustomsnapshotService } from '../snapshot/Services/customsnapshot.service';
import { SnapshotService } from '../snapshot/Services/snapshot.service';
import { CommonChartmoduleService } from '../snapshot/Services/common-chartmodule.service';
import { ConfigurationService } from 'src/app/common/services/configuration.service';
import { FilesWithIdentifiers } from 'src/app/common/models/configurations.model';
import { exceptionMessage } from 'src/app/common/constants/common-constants';
import { IndexedDBService } from 'src/app/common/services/askme_indexeddb.service';
import { protectedResources } from 'src/app/auth.config';
import { DiagnosticViewerHelper } from './diagnostic-viewer/diagnostic-viewer.helper';
// #endregion

// Helpers - orchestration pipeline
import { SessionStateHelper } from './helpers/orchestration/session-state.helper';
import { PayloadBuilderHelper } from './helpers/orchestration/payload-builder.helper';
import { Step2ProcessorHelper } from './helpers/orchestration/step2-processor.helper';
import { OrchestrationHelper } from './helpers/orchestration/orchestration.helper';
import { AfterSubmitHelper } from './helpers/orchestration/after-submit.helper';
import { PayloadServiceHelper } from './helpers/orchestration/payload-service.helper';
import { RedirectHelper } from './helpers/orchestration/redirect.helper';

// Helpers - chat session, history & messaging
import { ChatHistoryHelper } from './helpers/chat/chat-history.helper';
import { ChatHistorySidebarHelper } from './helpers/chat/chat-history-sidebar.helper';
import { ChatAnswerLoadingHelper } from './helpers/chat/chat-answer-loading.helper';
import { GetChatResponseHelper } from './helpers/chat/get-chat-response.helper';
import { SendInputMessageHelper } from './helpers/chat/send-input-message.helper';
import { MultiQuestionHelper } from './helpers/chat/multi-question.helper';

// Helpers - insights
import { InsightsGeneratorHelper } from './helpers/insights/insights-generator.helper';
import { InsightsStreamingHelper } from './helpers/insights/insights-streaming.helper';
import { InsightsApiHelper } from './helpers/insights/insights-api.helper';
import { OpenInsightsHelper } from './helpers/insights/open-insights.helper';

// Helpers - charts
import { ChartUtilitiesHelper } from './helpers/charts/chart-utilities.helper';
import { ChartClickHelper } from './helpers/charts/chart-click.helper';
import { ChartTooltipsHelper } from './helpers/charts/chart-tooltips.helper';

// Helpers - table & data shaping
import { TableBuilderHelper } from './helpers/table/table-builder.helper';
import { TableOutputProcessorHelper } from './helpers/table/table-output-processor.helper';
import { TransposeHideNaHelper } from './helpers/table/transpose-hidena.helper';
import { DataSortHelper } from './helpers/table/data-sort.helper';
import { DataTransformersHelper } from './helpers/table/data-transformers.helper';

// Helpers - content formatting & summaries
import { FormattersHelper } from './helpers/content/formatters.helper';
import { MarkdownPayloadHelper } from './helpers/content/markdown-payload.helper';
import { SelectionSummaryHelper } from './helpers/content/selection-summary.helper';

// Helpers - export
import { ExportHandlerHelper } from './helpers/exports/export-handler.helper';

// Helpers - UI interactions
import { ViewTogglerHelper } from './helpers/ui/view-toggler.helper';
import { UIEffectsHelper } from './helpers/ui/ui-effects.helper';
import { AnalysisViewSwitcherHelper } from './helpers/ui/analysis-view-switcher.helper';
import { ChatSearchHelper } from './helpers/chat-search/chat-search.helper';
import { DictationHelper } from './helpers/ui/dictation.helper';
import { FeedbackHelper } from './helpers/ui/feedback.helper';
import { CopyClipboardHelper } from './helpers/ui/copy-clipboard.helper';
import { DropdownHandlersHelper } from './helpers/ui/dropdown-handlers.helper';
import { RibbonConfigHelper } from './helpers/ui/ribbon-config.helper';
import { CHART_TYPES, DEFAULT_VALUES, SNAPSHOT_TYPES } from './constants/chatbot-expand.constants';

// #region Interfaces
interface Config {
  key: string;
  value: boolean;
}

interface VariableList {
  Level0: string;
  isEntryPoint: boolean;
  hasOnlyBrandIndependentChildren: boolean;
  hasOnlyBrandDependentChildren: boolean;
  hasSomeBrandDependentChildrenNotAll: boolean;
  Level1: {
    Level1_value: string;
    is_derived: string;
    is_brand_analysis: number;
    calc_type: string;
    tooltip: string;
    hasNextLevel: boolean;
    data_variable_code: string;
    response_code: string;
    sort_id: string;
    is_multi_punch: string;
    date: string;
    year_code: string;
    is_dimension: string;
    isCustomGrpPresent: string;
    isCustomCalcPresent: string;
    kibanaFilterScript: string;
    customGroupId: string;
    customCalculationId: string;
    dataYearCode: string;
    is_time_Period_Present: string;
  }[];
}

interface ListItem {
  Level0: string;
  Level1: string;
  tooltip: string;
  has_nextLevel: boolean;
  sortId: number;
  is_Derived: string;
  is_calc_type: string;
  data_variable_code: string;
  data_response_code: string;
  data_sort_id: string;
  data_is_multi_punch: string;
  data_date: string;
  data_year_code: string;
  is_dimension: boolean;
  is_custmGrpPresent: boolean;
  is_custmCalcPresent: boolean;
  kibanaFilterScript: string;
  customGroupId: string;
  customCalculationId: string;
  dataYearCode: string;
  isSelectAllMetrics: boolean;
  isSelectAllMetricsCount: number;
  is_entrypoint?: boolean;
}
// #endregion

@Component({
  selector: 'app-chatbotexpand',
  templateUrl: './chatbotexpand.component.html',
  styleUrls: ['./chatbotexpand.component.css'],
})
export class ChatbotexpandComponent implements OnInit, AfterViewInit, OnDestroy {

  // #region State & Properties

  private _isHistorySaving: boolean = false;
  get isHistorySaving(): boolean { return this._isHistorySaving; }
  set isHistorySaving(val: boolean) {
    if (this._isHistorySaving && !val) {
      this.triggerTabEnabledAnimation();
    }
    this._isHistorySaving = val;
  }
  isWaitingForFinalSave: boolean = false;
  isTabJustEnabled: boolean = false;
  private tabEnableTimeout: any;

  // --- Multi-Question State ---
  isMultiQuestionMode: boolean = false;
  multiQuestionList: string[] = [''];
  queuedQuestions: string[] = [];
  isProcessingQueue: boolean = false;
  unreadNotificationCount: number = 0;
  baseFaviconHref: string = '';

  public console = console;
  hasSnapshotActive: boolean = false;
  hasBrandAnalysisActive: boolean = false;
  hasCrosstabActive: boolean = false;
  errorHeader: string = '';

  @ViewChild('summaryText') summaryText: ElementRef | undefined;
  @ViewChild('tableOutputComponent') tableOutputComponent!: TableOuputComponent;
  @ViewChild('SidePanel') SidePanel: ElementRef | undefined;
  @ViewChild('inforRibbon', { static: false }) inforRibbon: ElementRef | undefined;
  @ViewChild('chatHistoryScroll') chatHistoryScroll: ElementRef | undefined;
  @ViewChildren('insightsContainer') insightsContainers!: QueryList<ElementRef>;
  @ViewChild('likeInput') likeInput: ElementRef | undefined;
  @ViewChild('likeFeedbackBox') likeFeedbackBox: ElementRef | undefined;
  @ViewChild('dislikeInput') dislikeInput: ElementRef | undefined;
  @ViewChild('dislikeFeedbackBox') dislikeFeedbackBox: ElementRef | undefined;
  @ViewChild('deleteConfirmBox') deleteConfirmBox: ElementRef | undefined;
  @ViewChild('outputArea') outputArea: ElementRef | undefined;
  @ViewChild('exportsPopup', { static: false }) exportsPopupRef: ElementRef | undefined;

  @Input() exportDownloads: boolean = true;
  @Input() pptExport: boolean = true;
  @Input() isIndex: boolean = false;
  @Input() landingdata: any;

  private isPreFetchingAnswers = false;
  private pendingMessageId: string | null = null;
  isScrollingToTarget = false;
  private tooltipStates = { stack: false, line: false, bar: false };
  private subscriptions: Subscription = new Subscription();

  // FORM & SEARCH PROPERTIES
  searchForm!: FormGroup;
  searchText: string = '';
  searchTextLevel1: string = '';
  searchQuery: string = '';
  inputValue: any = '';
  existingquery: any = '';
  chatSearchText: string = '';
  chatNestedSearchText: string = '';
  isNestedSearchVisible: boolean = false;
  primaryFilteredCount: number = 0;
  starredSearchText: string = '';
  scrollSpacerHeight: number = 0;

  // USER & SESSION MANAGEMENT
  userId: string = '';
  guidChatId: string = '';
  validatedSessionId: string | null = null;
  selectedChatId: string | null = null;
  selectedMessageId: string | null = null;
  newChatId: any;
  isNewChatStarted: boolean = false;
  abandoningSessionId: string = '';

  // STUDY & CLIENT CONFIGURATION
  selected_study: any;
  selected_client: any;
  studyname: string = '';
  clientcode: any;
  receivedData: StudyData[] = [];
  filteredlist: StudyData | undefined;
  filteredStudies: string[] = [];
  logo_url: string = '';
  isDemoStudy: boolean = false;
  arktabaccess: boolean = false;
  private inFlightPreFetches: { [messageId: string]: Set<number> } = {};
  private inFlightInsights: { [messageId: string]: Set<number> } = {};
  modulesAccess: any = null;
  isCacheGenerated: boolean = false;
  streamStatusText: string = '';
  isStreamFinished: boolean = false;
  statusQueue: string[] = [];
  private isProcessingStatusQueue: boolean = false;
  chatPayload: any = null;
  parallelChatPayload: any = null;
  private currentOrchToken: string = '';

  // Multi-View Support
  availableViews: any[] = [];
  activeView: any = null;
  allMemberResponses: any = null;
  orchestrationRawData: any = null;
  private multiViewInsightsCache: { [messageId: string]: { [mcode: number]: { insightsVal: any, qnaText: string, rawInsights?: string, standard_insights?: string, trend_insights?: string } } } = {};
  private multiViewDataCache: { [messageId: string]: { [mcode: number]: any } } = {};

  // CHAT & CONVERSATION PROPERTIES
  public isIdentityLoaded: boolean = false;
  public isChatListLoaded: boolean = false;
  public isHistoryReady: boolean = false;

  public checkHistoryReady() {
    this.isHistoryReady = this.isIdentityLoaded && this.isChatListLoaded;
  }

  private _currentView: 'landing' | 'chat' | 'loading' = 'landing';
  get currentView(): 'landing' | 'chat' | 'loading' { return this._currentView; }
  set currentView(value: 'landing' | 'chat' | 'loading') {
    if (this._currentView !== value) {
      this._currentView = value;
      this.isMultiQuestionMode = false;
    }
  }
  isChatHistoryOpen: boolean = false;
  isSavedQuestionsOpen: boolean = false;
  isChatAreaVisible: boolean = false;
  private currentTrendlineResponse: any = null;
  private currentRowPayloadTrendline: any = null;
  private currentCustomizationTrendline: any = null;
  private metadataFetchPromise: Promise<void> | null = null;
  hasPendingQuery: boolean = false;
  currentQueryForStream: string = '';
  isAnalysisSwitching: boolean = false;
  isTrendAvailable: boolean = false;
  isTrendActive: boolean = false;

  isNewChatSession: boolean = false;
  isClosing: boolean = false;
  chatList: ChatListDocument[] = [];
  allChatList: ChatListDocument[] = [];
  chatHistory: ChatHistoryDocument[] = [];
  recentChats: ChatListDocument[] = [];
  olderChats: ChatListDocument[] = [];

  // Speech Recognition (Dictation) Feature
  isSpeechSupported: boolean = false;
  isDictating: boolean = false;
  activeDictationTarget: 'main' | 'chatSearch' | 'nestedSearch' | null = null;
  speechRecognition: any;
  baseInputValue: string = '';

  // Chat Search Feature
  isChatSearchOpen: boolean = false;
  chatSearchQuery: string = '';
  filteredSearchChats: any[] = [];
  searchDebounce$ = new Subject<string>();
  searchSelectedIndex: number = -1;

  chatResponse: { [key: string]: string }[] = [];
  chatAccordions: any[] = [];
  chatPreviewBlocks: {
    question: string;
    response: string;
    messageId?: string;
    isTableLoading?: boolean;
    isPPTExportLoading?: boolean;
    isExcelExportLoading?: boolean;
    isTemp?: boolean;
    isStarred?: boolean;
    isLiked?: boolean | null;
    feedbackSaved?: boolean;
    showLikeInput?: boolean;
    likeReason?: string;
    showDislikeInput?: boolean;
    dislikeReason?: string;
    showDeleteConfirm?: boolean;
    QuestionAnswer?: string;
    fullAnswer?: any;
    parsedData?: any;
    mcode?: number;
    analyticalData?: any;
    insights?: string[];
    insightsTrend?: string[];
    isTrendActive?: boolean;
    isTrendAvailable?: boolean;
    failedCase?: boolean;
    builtTableData?: any;
    builtCustomData?: any;
    isInsightsOverflowing?: boolean;
    isAtTop?: boolean;
    isAtBottom?: boolean;
    // Isolated UI State Flags
    isExportspopup?: boolean;
    sampleSizeDropdown?: boolean;
    confidenceLevelDropDown?: boolean;
    referenceLevelDropDown?: boolean;
    baseDropDown?: boolean;
    weightDropDown?: boolean;
    isReference?: boolean;
    // Isolated Analytical Parameters
    isSig?: boolean;
    isIndex?: boolean;
    DefaultSampleSize?: string;
    SignificanceVsReference?: string;
    DefaultConfidenceLevel?: string;
    DefaultReferenceLevel?: string;
    selectedWeight?: string;
    DefaultWeight?: string;
    DefaultBase?: string;
    isTotalBase?: boolean;
    isAnswerBase?: boolean;
    isTranspose?: boolean;
    isHideNA?: boolean;
    isTotal?: boolean;
    isSampleSize?: boolean;
    isWeightedLabel?: boolean;
    isWeightedNumerator?: boolean;
    isUnWeightedNumerator?: boolean;
    isSampleSizeBlock?: boolean;
    isWeightedSampleBlock?: boolean;
    isPairWise?: boolean;
    isDisablePairWise?: boolean;
    isDisableReference?: boolean;
    is_confidenceLevel?: any;
    is_TransposeBtn?: boolean;
    threshold?: string;
    selectedIndex?: string;
    is_sigtesting?: boolean;
    is_Time_Period_Present?: boolean;
    dataDropDown?: boolean;
    DefaultDataDropDown?: string;
    isTableClick?: boolean;
    isVerticalStackChartClick?: boolean;
    isVerticalBarChartClick?: boolean;
    isVerticalGroupBarChartClick?: boolean;
    isLineChartClick?: boolean;
    isChartView?: boolean;
    disableStackChart?: boolean;
    disableLineChart?: boolean;
    showStackTooltip?: boolean;
    showLineTooltip?: boolean;
    showBarTooltip?: boolean;
    showMainTooltip?: boolean;
    StackFlag?: boolean;
    displayTable?: boolean;
    isTableClosing?: boolean;
    isChartClosing?: boolean;
    isBrandAnalysis?: boolean;
    isAnalysisSwitching?: boolean;
    selectedDataFormat?: string;
    selectionSummary?: string;
    insightsVal?: any;
    // Isolated Chart Objects
    StackChart?: StackChart;
    LineChart?: LineChart;
    BarChart?: GroupBarChart;
    // Isolated Data Storage
    OutputdataObject?: DataObject;
    CrossTabOutPutData?: CrossTabOutputItem[];
    CrossTabOutPutEntireData?: any[];
    dataObject?: any;
    IndexData?: any[];
    WeightDropDownList?: any[];
    SampleSizeList?: any[];
    variableTabData?: any[];
    // Isolated View Switcher State
    availableViews?: any[];
    activeView?: any;
    searchTextLevel1?: string;
    userSelectedMcode?: boolean;
    snapshotPayload?: any;
    crosstabPayload?: any;
    brandAnalysisPayload?: any;
  }[] = [];
  private defaultQuestions: string[] = [
    'Brand funnel of Bluehold?',
    'Compare the mean brand power grid and consideration for Bluehold Financial and Guardia Funds in Q3 2023 in Market 01?',
    'Familiarity of Silveridge among females?',
    'Among male respondents aged 25-34 in Market 01, what is the awareness of Sproutvest Finance?'
  ];
  private brownformanQuestions: string[] = [
    'Compare Consideration of Absolut and Bacardi in Canada?',
    'Among Males and Females, Compare the Awareness of Smirnoff in Australia?',
    'Compare Affinity of Jameson and Glenfiddich in UK?',
    'What is the Buzz score for Absolut among consumers aged 22 to 24?'
  ];

  private optusQuestions: string[] = [
    'Compare the Masterbrand Awareness of Optus and Vodafone?',
    'Differentiate all the Masterbrand Considerations of Telstra among Male and Females?',
    'Compare all levels of Masterbrand Meets Needs of Amaysim and Dodo in the Consumer Survey?',
    'Masterbrand Usage of TPG and Aussie Broadband among Age Bands 18 to 29 and 30 to 44?'
  ];

  private asicsQuestions: string[] = [
    'Imagery of Adidas in Canada?',
    'Awareness of ASICS among males for Q2 in Australia and China?',
    'Most Preferred choice of New Balance and Puma among Male and Female in UK?',
    'Brand* Net Promoter Score (ASICS) of different Australia Cities like Sydney and Melbourne?'
  ];

  private grabQuestions: string[] = [
    'Used (P1M) of Grab in 2025 and 2026 in Malaysia?',
    'Grab Brand Selected for CRI among Low, Middle and High Income groups?',
    'Compare Grab and Meta with Brand Selected for CRI among Singapore and Indonesia for the past 3 months from May 2026 onwards?',
    'Awareness of Grab and Shopee among Social Media Platform Used (P1M) of Youtube, Instagram, TikTok and Facebook in Vietnam in 2026?'
  ];

  private dysonQuestions: string[] = [
    'TBCA of Dyson among Males?',
    'Compare the Imagery of BaByliss and Cloud Nine in Q1 2025?',
    'Ownership of Dyson in the month of November 2025 of VC Category?',
    'Loyalty Metrics of GHD and Revlon?'
  ];

  defaultQuestionAnswer: string = 'Answering question...';

  get questions(): string[] {
    if (this.selected_client?.toLowerCase() === 'optus') {
      return this.optusQuestions;
    }
    if (this.selected_client?.toLowerCase() === 'asics') {
      return this.asicsQuestions;
    }
    if (this.selected_client?.toLowerCase() === 'brown forman') {
      return this.brownformanQuestions;
    }
    if (this.selected_client?.toLowerCase() === 'grab') {
      return this.grabQuestions;
    }
    if (this.selected_client?.toLowerCase() === 'dyson') {
      return this.dysonQuestions;
    }
    return this.defaultQuestions;
  }

  // STARRED/SAVED QUESTIONS
  starredMessages: { MessageId: string; Question: string; ChatId: string; CreatedAt?: string; QuestionAnswer?: string; AnswerPreview?: string }[] = [];
  filteredStarredMessages: { MessageId: string; Question: string; ChatId: string; CreatedAt?: string; QuestionAnswer?: string; AnswerPreview?: string }[] = [];
  isLoadingStarredMessages: boolean = false;
  inFlightLazyFetches: Set<string> = new Set<string>();

  // UI DISPLAY FLAGS - GENERAL
  displayTable: boolean = false;
  isTableView: boolean = false;
  isChartView: boolean = true;
  private maxRowsPerSlide = 15;
  private maxColumnsPerSlide = 28;
  isTableClick: boolean = false;
  isListView: boolean = true;
  showSnapshot: boolean = false;
  snapshotConfiguration: any = null;
  showExtraSelections: boolean = false;
  showModuleDropdown: boolean = true;
  showDropdown: boolean = false;
  showCustomMetricsPopup: boolean = false;
  showToastNotification: boolean = false;
  showOutput: boolean = true;
  askMeLanding: boolean = true;
  isHidden: boolean = true;

  noContentFlag: boolean = false;
  isInsightspopupOpen: boolean = false;
  activeHighlightTerm: string = '';
  isExportspopup: boolean = false;
  isCreateBtnClick: boolean = false;
  isInsightsBtnEnabled: boolean = false;
  isselectionsummarycollapse: boolean = false;
  isselectionsummarycollapse2: boolean = false;
  issavedselectionblock: boolean = false;
  isSaveSelectionClick: boolean = false;
  isSaveCustomGroupSelectionClick: boolean = false;
  isSaveCustomCalculationSelectionClick: boolean = false;
  isDiagnosticAnimating: boolean = false;
  isDiagnostic: boolean = false;
  moduleSelectionToggle: boolean = false;
  failedCase: boolean = false;

  // UI DISPLAY FLAGS - PANELS
  sidePanelDisplay: boolean = false;
  sidePanelNestingDisplay: boolean = false;
  columnPanelDisplay: boolean = false;
  columnNestingPanelDispaly: boolean = false;
  filterPanelDisplay: boolean = false;
  FooterNote: boolean = false;
  filteringAnsBlock: boolean = false;
  AnsBaseBlock: boolean = false;

  // UI DISPLAY FLAGS - DROPDOWNS  
  weightDropDown: boolean = false;
  dataDropDown: boolean = false;
  confidenceLevelDropDown: boolean = false;
  referenceLevelDropDown: boolean = false;
  sampleSizeDropdown: boolean = false;
  ConfidenceLevelDropDownToggle: boolean = false;
  BaseDropDownToggle: boolean = false;
  weightDropDownToggle: boolean = false;
  SampleSizeContainer: boolean = false;

  // CHART & VISUALIZATION  
  isTranspose: boolean = false;
  isHideNA: boolean = false;
  isTotal: boolean = false;
  chartType = this.isTranspose ? CHART_TYPES.BAR : CHART_TYPES.COLUMN;
  is_TransposeBtn: boolean = false;
  is_chart_visualization: boolean = false;
  isVerticalBarChartClick: boolean = false;
  isLineChartClick: boolean = false;
  isVerticalStackChartClick: boolean = false;
  isVerticalGroupBarChartClick: boolean = false;
  disableStackChart: boolean = false;
  disableLineChart: boolean = false;
  StackFlag: boolean = false;
  showBarTooltip: boolean = false;
  showStackTooltip: boolean = false;
  showLineTooltip: boolean = false;
  showMainTooltip: boolean = false;
  public LineChart: LineChart = {
    title: '',
    Data: [],
    Properties: [],
  };
  public StackChart: StackChart = {
    title: '',
    Data: [],
    Properties: [],
  };
  public BarChart: GroupBarChart = {
    title: '',
    Data: [],
    Properties: [],
  };
  GeneralToolTip: string[] = [
    "<ul>Charts are automatically disabled under the following conditions:<li>Indexing or nesting is applied </li><li>More than 15 options are selected in Rows</li> <li>Metrics from both percentage and mean logic are selected at the same time</li></ul>"
  ];
  StackChartToolTip: string[] = [
    "Stack Chart is enabled only when a single metric group is selected with all its options, and the number of rows does not exceed 15. It won’t work for multipunch variables or if nesting is applied."
  ];
  LineChartToolTip: string[] = [
    "Trend Line is available only when one Time Period Group is in Columns, with each Row option plotted as separate trend. A maximum of 15 options is allowed in Rows; charting is disabled for multipunch variables, multiple metrics, or when row/column nesting is applied."
  ];
  BarChartToolTip: string[] = [
    "Bar Chart allows up to 15 options in Rows. Charting is disabled if multiple metrics are selected or if row/column nesting is applied."
  ];

  // DATA ARRAYS & LISTS
  dataArray: any[] = [];
  fomDataArray: any[] = [];
  leftpanelDataArray: any[] = [];
  leftPanelData: any = {};
  loadData: any[] = [];
  listAItemsList: ListItem[] = [];
  listBItemsList: ListItem[] = [];
  listCItemsList: ListItem[] = [];
  listDItemsList: ListItem[] = [];
  listEItemsList: ListItem[] = [];
  entryList: any[] = [];
  entryPanel: { Level1: string }[] = [];
  variableList: VariableList[] = [];
  SampleSizeList: any = [];
  aggregatedVariables: any = [];
  is_confidenceLevel: any = [];
  WeightDropDownList: { Label: string; responseCode: string; tooltip: string }[] = [];
  dataDropDownList: string[] = ['Data', 'Sample Size', 'Weighted Sample', 'Weighted Numerator', 'Unweighted Numerator'];
  titleSection: { key: string; value: string }[] = [];
  entryPoints: { key: string; value: string }[] = [];
  selections: { key: string; value: string }[] = [];
  Columnselections: any = [];
  Rowselections: any = [];
  AllSelections: any = [];
  moduleseSelections = [
    { key: 'Snapshot', value: true, isSelected: true },
    { key: 'Crosstab', value: true, isSelected: false }
  ];
  snapshotWidgetData: any = [];
  snapshotDataModel: any = [];
  moduleList: any = [];
  indexVsBrandReference: any = [];
  significanceVsBrandReference: any = [];
  subSectionNames: any = [];
  weight: any = [];
  base = [
    { key: 'Total Base', selected: true },
    { key: 'Answer Base', selected: false }
  ];
  base2 = [{ key: 'Total Base', selected: true }];
  vsBrandArray: any[] = [];
  selectedSigType: any[] = [];
  significanceTypes: any[] = [];
  significanceVal: any[] = [];

  // DATA OBJECTS & COMPLEX STRUCTURES
  previewDataObject: DataObject = {
    Row: [],
    Column: [],
    RowNesting: [],
    ColNesting: [],
    Filter: [],
    CustomGroup: [],
    CustomCalculation: []
  };
  FinalOutputdataObject: OutputDataObject = {
    Row: [],
    Column: []
  };
  public OutputdataObject: OutputDataObject = {
    Row: [],
    Column: [],
  };
  public dataObject: DataObject = {
    Row: [],
    Column: [],
    RowNesting: [],
    ColNesting: [],
    Filter: [],
    CustomGroup: [],
    CustomCalculation: [],
  };
  public fomData: fomData = {
    Header: '',
    Level1: '',
    Level2: '',
    MetricOption: '',
    data_variable_code: '',
    response_code: '',
    derived_metric_calculation: '',
    Level0: '',
    Level3: '',
    Level4: '',
    Level5: '',
    Level6: '',
    sort_id: 0,
    level1_sort_id: 0,
    is_multi_punch: 0,
    is_derived_variable: false,
    is_dimension: false,
    calculation_type: '',
    url_logo: '',
    tooltip: '',
    year_code: '',
    year_label: '',
    date: '',
    isActive: false,
    is_CustomGroup: false,
    customGroupQuery: '',
    is_CustomCalculation: false,
    customCalculationQuery: '',
    dataYearCode: '',
    is_time_Period_Present: false,
    is_dimension_Present: false,
    is_shared: false,
    user_name: '',
    customGroupId: '',
    customCalculationId: '',
    is_brandanalysis: 0,
    variable_code: '',
    is_aggregated_data: 0,
    is_semi_aggregated_data: 0,
    is_snapshot: 0,
    sequence: 0,
  };
  widegtconfigdetails: any = {
    section: '',
    subSection: '',
    snapshotType: '',
    snapshotName: '',
    clientName: '',
    study: '',
    IsCustomSnapshot: false
  };
  output_details: any = {};
  weightFilter: { [key: string]: string } = {};

  // CONFIGURATION & SETTINGS
  variableTabData: any;
  configWeights: any;
  configweights: any;
  DefaultConfidenceLevel: string = DEFAULT_VALUES.NONE;
  DefaultSampleSize: string = DEFAULT_VALUES.NONE;
  DefaultDataDropDown: string = DEFAULT_VALUES.DATA;
  DefaultReferenceLevel: string = DEFAULT_VALUES.NONE;
  DefaultWeight: string = '';
  DefaultBase: string = 'Total Base';
  selectedDataFormat: string = '';
  selectedWeight: string = '';
  selectedIndex: string = '';
  threshold: string = '';
  isData: boolean = true;
  isWeightedLabel: boolean = false;
  isSampleSize: boolean = false;
  isSampleSizeBlock: boolean = false;
  isWeightedSampleBlock: boolean = false;
  isWeightedBase: boolean = false;
  isWeightedNumerator: boolean = false;
  isUnWeightedNumerator: boolean = false;
  isTotalBase: boolean = false;
  isTotalBaseFromPrompt: string = 'false';
  isAnswerBase: boolean = false;
  isFilteringActive: boolean = true;
  isRowNesting: boolean = false;
  isColumnNesting: boolean = false;
  isSelectAllMetricsLevel: boolean = false;
  isSelectAllMetrics: any;
  isAggregatedData: boolean = false;
  isSemiAggregatedData: boolean = false;
  enableAnswerBase: boolean = true;
  isWeigthedBtn: boolean = true;
  isExistingDataPresent: boolean = false;
  columnLength: number = 0;

  is_sticky_selection: boolean = false;
  isRespondentLevel: boolean = true;

  // SIGNIFICANCE TESTING & STATISTICAL
  isPairWise: boolean = false;
  isReference: boolean = false;
  isDisablePairWise: boolean = true;
  isDisableReference: boolean = true;
  isSignificanceContainerDisabled: boolean = false;
  is_sigtesting: boolean = false;
  isSig: boolean = true;
  isSigSelected: any = true;
  SignificanceVsReference: string = '';
  significance: string = 'None';
  displaySignificance: any = 'None';
  displayConfidenceLevel: string = '';
  displayReference: any = 'None';
  confidenceLevelSelectedValue: string = '';
  significanceSelectedValue: string = 'None';
  selectedSignificance: any;
  selectedSigBrand: any;
  stacticText: string = '';

  // WEIGHT & INDEX CONFIGURATION
  is_indexing: boolean = false;
  is_indexingShow: boolean = false;
  isIndexSelected: any;
  IndexData: any;
  weightColumnName: string = '';
  weightSelectedValue: any = 'Total Population';
  sortToggle: boolean = true;

  // SNAPSHOT & WIDGET CONFIGURATION
  snapshotName: string = '';
  selectedType: any = 'Single Brand';
  filteredWidgetSelection: any;
  pageData: any;
  widgetData: any;
  baseselected: string = 'Total Base';
  outputData: any;
  item: any;
  items: any;
  itemWithoutTrend: any;
  itemWithoutTrendItems: any;
  lastCrosstabPayload: any;
  lastSnapshotPayload: any;
  lastBrandAnalysisPayload: any;
  MainResult: any;
  masterStudyConfig: any = null;
  filteredWidgetData: any;
  filteredWidgetID: any;
  selectedWidgetName: any;
  selectedWidgetID: any;
  WidgetId: any;
  Widget_Id: any;
  widget_Name: any;
  widget_Id: any;
  displayModuleSelected: string = 'Snapshot';
  selectedSection: string = '';
  selectedSubSection: string = 'NA';
  selectedcompareacrossOption: string = '';
  SaveSelectiontext: string = '';
  isselectionHeader: string = '';

  // EXPORT & DOWNLOAD PROPERTIES
  excelExport: boolean = false;
  CrossTabOutPutData: any;
  Snapshotdataforexport: any;
  CrossTabOutPutEntireData: any;
  OutputData: any;
  passOutputData: any;
  ImageURL: string = '';

  // BRAND ANALYSIS
  is_brand_analysis: boolean = false;
  isBrandAnalysis: boolean = false;
  isBoostFactor: boolean = false;
  ShareColumn: any;
  BaseColumn: any;
  selectedBrands: any[] = [];
  referenceBrand: string = '';
  allbrands: any;
  allSigBrands: any;

  // TOAST NOTIFICATIONS
  toastMessage: string = '';
  toastType: 'success' | 'error' | 'info' = 'success';
  toastTimeoutId: any;
  isToastHovered: boolean = false;
  toastRemainingTime: number = 3000;
  toastStartTime: number = 0;

  // INSIGHTS & ANALYTICS
  copiedInsightsId: string | null = null;
  copiedResponseId: string | null = null;

  // DIAGNOSTIC & ICONS
  diagnosticIcon: string = 'assets/imageChatbot/Diagnostic_Left_Icon_Normal.svg';

  // SELECTION & SUMMARY
  selectionSummary: string = '';
  selectionSummaryTitle: string = '';
  summaryTitle: any;
  additionalList: any;

  // INSIGHTS
  insightsVal: any = '';
  InsightsResponse: any = '';
  public insights_payload: any = null;
  public snapshotinsights_payload: any = null;
  public snapshotinsights_trend_payload: any = null;
  public snapshotinsights_standard_text: string = '';
  public snapshotinsights_trendline_text: string = '';
  public insightsStreamingText: string = '';
  public insightsStreamingPoints: string[] = [];
  clientLogo: string | null = null;
  isBrandingLoaded: boolean = false;
  private insightsScrollInterval: any = null;
  private isInsightsScrolling: boolean = false;
  public isInsightsStreaming: boolean = false;
  private qnaFirstChunkTime: number = 0;

  // COPY PROPERTIES
  copiedChartId: any = null;
  isCopyingId: any = null;

  // MISCELLANEOUS/LEGACY PROPERTIES
  is_Transpose: any;
  is_ranking: any;
  isFilterToggle: any;
  isAnswerToggle: any;
  comparisionpoint: any;
  confidenceLevel: any;
  repository: any;
  lowBaseIdentifier: any;
  is_AI_Integration: any;
  is_StoryBoard: any;
  is_Column_Row_Percentage: any;
  insufficentBase: any;
  insufficientBase: any;
  lowBase: any;
  LowBase: any;
  iscustomgrp: any;
  iscustomCalc: any;
  iscustomreport: any;
  changeEntryPoint: any;
  is_reset_selection: any;
  dataexists: any;
  newdataexists: any;
  strdata: any;
  serviceVariable: any;
  mcode: any;
  promptData: any;
  mandatoryVariableMapping: any;
  is_dimension_Present: boolean = false;
  is_Time_Period_Present: boolean = true;
  istimePeriodWave: boolean = false;

  // SUBSCRIPTIONS
  $unSubscribeSubmitData1!: Subscription;
  $unSubscribeSubmitData2!: Subscription;
  $unSubscribeSubmitData3!: Subscription;
  $unSubscribeSubmitData4!: Subscription;
  $unSubscribeSubmitData5!: Subscription;
  $unSubscribeSubmitData6!: Subscription;
  $unSubscribeSubmitData7!: Subscription;
  $unSubscribeSubmitData8!: Subscription;
  $unSubscribeSubmitData9!: Subscription;

  isSummaryForSnapshot: boolean = false;

  // Multi-question state
  showTemplatePrompt: boolean = false;
  pendingFileInput: HTMLInputElement | null = null;

  // Chart/table close-transition state
  isChartClosing: boolean = false;
  isTableClosing: boolean = false;

  // Chat delete-confirmation state
  chatToDelete: any = null;
  isDeletingChat: boolean = false;

  private userIdentityPromise: Promise<void> | null = null;

  get currentQuestion(): string {
    const selectedBlock = this.selectedMessageId ? this.chatPreviewBlocks.find(b => b.messageId === this.selectedMessageId) : null;
    return selectedBlock?.question || this.chatPreviewBlocks[this.chatPreviewBlocks.length - 1]?.question || '';
  }

  get currentAnswer(): string {
    const selectedBlock = this.selectedMessageId ? this.chatPreviewBlocks.find(b => b.messageId === this.selectedMessageId) : null;
    return selectedBlock?.QuestionAnswer || this.chatPreviewBlocks[this.chatPreviewBlocks.length - 1]?.QuestionAnswer || '';
  }

  get isAnyLoaderActive(): boolean {
    return this.arktabaccess || this.isScrollingToTarget || this.isInsightsStreaming;
  }

  // #endregion

  // #region Constructor & Lifecycle

  constructor(
    private router: Router,
    private fb: FormBuilder,
    public _loaderService: LoaderService,
    private _exportDownloads: ExportDownloadServices,
    private apiService: ChatbotService,
    private crossTabService: CrosstabService,
    private _insightsSerive: InsightspopupService,
    private _usermanagementService: UserProjectManagementService,
    private _configCustomService: CustomsnapshotService,
    public _configService: SnapshotService,
    private _configurationService: ConfigurationService,
    private sharedChatService: SharedChatService,
    private commonchartmodules: CommonChartmoduleService,
    private indexedDBService: IndexedDBService,
    private msalService: MsalService,
    private cdr: ChangeDetectorRef
  ) {
    this.landingdata = this.router.getCurrentNavigation()?.extras.state;
    if (this.landingdata == undefined) {
      this.router.navigateByUrl('landing');
    }
    this.searchForm = this.fb.group({
      query: ['']
    });

    const fromLandingPage = this.landingdata?.fromLandingPage === true || sessionStorage.getItem('fromLanding') === 'true';
    if (fromLandingPage) {
      this.currentView = this.landingdata?.inputValue ? 'chat' : 'landing';
    } else {
      this.currentView = 'chat';
    }
  }

  formatMarkdown(text: any, isInsights: boolean = false): string {
    if (Array.isArray(text)) {
      text = text[0] || '';
    }
    if (typeof text !== 'string') {
      return '';
    }
    return FormattersHelper.formatMarkdown(text, isInsights);
  }

  subscribeToVariableData(): void {
    this.$unSubscribeSubmitData3 =
      this.crossTabService.variableTabData$.subscribe(({ data }) => {
        if (data !== null && data !== undefined) {
          this.variableTabData = data;
          this.isAggregatedData = data[0].is_aggregated_data == 1;
          this.isSemiAggregatedData = data[0].is_semi_aggregated_data == 1;
          if (this.configWeights && this.configWeights.length > 0) {
            this.populateWeightDropdown();
          }
        }
      });
  }

  // --- Speech Recognition (Dictation) Methods ---
  initSpeechRecognition(): void {
    return DictationHelper.initSpeechRecognition(this);
  }

  toggleDictation(target: 'main' | 'chatSearch' | 'nestedSearch'): void {
    return DictationHelper.toggleDictation(this, target);
  }

  stopDictation(): void {
    return DictationHelper.stopDictation(this);
  }
  // ----------------------------------------------

  async ngOnInit(): Promise<void> {
    let session_id_from_storage = sessionStorage.getItem('session_id');

    if (this.landingdata && this.landingdata.guidChatId) {
      this.guidChatId = this.landingdata.guidChatId;
      this.validatedSessionId = this.guidChatId;
      this.modulesAccess = this.landingdata.modules_access;
    } else if (session_id_from_storage) {
      this.guidChatId = session_id_from_storage;
      this.validatedSessionId = this.guidChatId;
    }

    this.selected_study = this.landingdata?.study || null;
    this.selected_client = this.landingdata?.client_code || null;
    this._configService.clientName = this.selected_client;
    this._configService.selectedStudy = this.selected_study;

    this.preFetchMetadata();
    this.setupSearchDebounce();
    this.initSpeechRecognition();

    SessionStateHelper.performAccessCheck(this);

    this.askMeLanding = true;
    this.selected_study = this.landingdata?.study || null;
    this.selected_client = this.landingdata?.client_code || null;

    SessionStateHelper.loadBranding(this);

    this.commonchartmodules.colortemplate(this.selected_client);
    this.ShareColumn = this.landingdata?.shareColumn || null;
    this.BaseColumn = this.landingdata?.baseColumn || null;
    this.displayTable = false;
    this.subscribeToVariableData();

    setTimeout(() => {
      this.indexedDBService.clearExpiredCache().catch((err: any) =>
        console.error('Error clearing expired cache:', err)
      );
      this.getChatListData();
      this.initializeUserIdentity();
    }, 0);

    const fromLandingPage = this.landingdata?.fromLandingPage === true || sessionStorage.getItem('fromLanding') === 'true';
    if (!this.selected_study || !this.selected_client) {
      this.router.navigateByUrl('/landing');
      return;
    }

    this.fetchStudyDataAsync(fromLandingPage);
    this.summaryTitle = this.Summary();
  }

  ngAfterViewInit(): void {
  }

  ngAfterViewChecked(): void {
    if (this.isInsightspopupOpen && this.insightsContainers) {
      this.insightsContainers.forEach((container: ElementRef, index: number) => {
        const block = this.chatPreviewBlocks[index];
        if (block) {
          this.checkInsightsOverflow(block, container.nativeElement);

          if (this.isInsightsStreaming && block.messageId === this.selectedMessageId) {
            container.nativeElement.scrollTop = container.nativeElement.scrollHeight;
          }
        }
      });
    }
  }

  ngOnDestroy() {
    try {
      document.removeEventListener('click', this.onDocumentClick, true);
    } catch (e) {
    }
    this.subscriptions.unsubscribe();
    if (this.$unSubscribeSubmitData1) {
      this.$unSubscribeSubmitData1.unsubscribe();
    }
    if (this.$unSubscribeSubmitData2) {
      this.$unSubscribeSubmitData2.unsubscribe();
    }
    if (this.$unSubscribeSubmitData3) {
      this.$unSubscribeSubmitData3.unsubscribe();
    }
    if (this.$unSubscribeSubmitData4) {
      this.$unSubscribeSubmitData4.unsubscribe();
    }
    if (this.$unSubscribeSubmitData5) {
      this.$unSubscribeSubmitData5.unsubscribe();
    }
    if (this.$unSubscribeSubmitData6) {
      this.$unSubscribeSubmitData6.unsubscribe();
    }
    if (this.$unSubscribeSubmitData8) {
      this.$unSubscribeSubmitData8.unsubscribe();
    }
    if (this.$unSubscribeSubmitData9) {
      this.$unSubscribeSubmitData9.unsubscribe();
    }
    this.stopInsightsAutoScroll();
    sessionStorage.removeItem('isarkTabAccessed');
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    return UIEffectsHelper.handleKeyboardEvent(this, event);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    UIEffectsHelper.onDocumentClick(this, event);
  }
  // #endregion

  // #region Initialization & Study Data
  handleConfig(eventData: any): void {
    return SessionStateHelper.handleConfig(this, eventData);
  }

  getBearerToken(): Promise<string> {
    return SessionStateHelper.getBearerToken(this);
  }

  private async fetchStudyDataAsync(fromLandingPage: boolean) {
    return SessionStateHelper.fetchStudyDataAsync(this, fromLandingPage);
  }

  preFetchMetadata(): Promise<void> {
    return ChatHistoryHelper.preFetchMetadata(this);
  }

  handleMetadataPreFetchResponse(data: any): void {
    SessionStateHelper.handleMetadataPreFetchResponse(this, data);
  }

  initializeUserIdentity(): Promise<void> {
    return SessionStateHelper.initializeUserIdentity(this);
  }

  private async _initializeUserIdentityCore(): Promise<void> {
    return SessionStateHelper._initializeUserIdentityCore(this);
  }

  async createNewUserIdentity(): Promise<void> {
    return ChatHistoryHelper.createNewUserIdentity(this);
  }
  // #endregion

  // #region Chat Session, History & Multi-Question
  processSendMessageRequest(): void {
    SendInputMessageHelper.processSendMessageRequest(this);
  }

  toggleChatHistory(): void {
    return ChatHistorySidebarHelper.toggleChatHistory(this);
  }

  toggleSavedSelections(): void {
    return ChatHistorySidebarHelper.toggleSavedSelections(this);
  }

  loadStarredMessages(): void {
    return ChatHistorySidebarHelper.loadStarredMessages(this);
  }

  onStarredSearch(): void {
    return ChatSearchHelper.onStarredSearch(this);
  }

  unstarFromSaved(message: any, event: Event): void {
    return ChatHistorySidebarHelper.unstarFromSaved(this, message, event);
  }

  deleteFromSaved(message: any, event: Event): void {
    return ChatHistorySidebarHelper.deleteFromSaved(this, message, event);
  }

  onStarredQuestionClick(message: { MessageId: string; Question: string; ChatId: string }): void {
    return ChatHistorySidebarHelper.onStarredQuestionClick(this, message);
  }

  onSendMessage(): void {
    return ChatHistoryHelper.onSendMessage(this);
  }

  scrollToBottom(): void {
    setTimeout(() => {
      if (this.chatHistoryScroll && this.chatHistoryScroll.nativeElement) {
        const element = this.chatHistoryScroll.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    }, 100);
  }

  scrollToMessage(messageId: string | undefined, align: 'start' | 'end' = 'end'): void {
    UIEffectsHelper.scrollToMessage(this, messageId, align);
  }

  toggleView(): void {
    this.isListView = !this.isListView;
  }

  trackByIndex(index: number): number {
    return index;
  }

  getGridImage(index: number): string {
    const imageNumber = (index % 6) + 1;
    return `url('/assets/ImagesAskme/Grid_${String(imageNumber).padStart(2, '0')}.jpg')`;
  }

  truncateText(text: string, maxLength: number = 150): string {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength).trim() + '...';
  }

  navigateToChat(): void {
    return UIEffectsHelper.navigateToChat(this);
  }

  navigateToLanding(): void {
    return UIEffectsHelper.navigateToLanding(this);
  }

  onCloseChatHistory(): void {
    return ChatHistorySidebarHelper.onCloseChatHistory(this);
  }

  onBackClickChatHistory(): void {
    return ChatHistorySidebarHelper.onBackClickChatHistory(this);
  }

  toggleAccordion(item: any): void {
    return ChatHistorySidebarHelper.toggleAccordion(this, item);
  }

  toggleMonthGroup(group: any): void {
    return ChatHistorySidebarHelper.toggleMonthGroup(this, group);
  }

  onChatHeaderQuestionClick(item: any): void {
    return ChatHistorySidebarHelper.onChatHeaderQuestionClick(this, item);
  }

  onCloseSavedQuestions(): void {
    return ChatHistorySidebarHelper.onCloseSavedQuestions(this);
  }

  onBackClickSavedQuestions(): void {
    return ChatHistorySidebarHelper.onBackClickSavedQuestions(this);
  }

  toggleMultiQuestionMode(): void {
    return MultiQuestionHelper.toggleMultiQuestionMode(this);
  }

  addMultiQuestion(): void {
    return MultiQuestionHelper.addMultiQuestion(this);
  }

  promptForTemplate(fileInput: HTMLInputElement): void {
    return MultiQuestionHelper.promptForTemplate(this, fileInput);
  }

  handleTemplateSelection(download: boolean): void {
    return MultiQuestionHelper.handleTemplateSelection(this, download);
  }

  onBatchExcelUpload(event: Event): void {
    return MultiQuestionHelper.onBatchExcelUpload(this, event);
  }

  removeMultiQuestion(index: number): void {
    return MultiQuestionHelper.removeMultiQuestion(this, index);
  }

  get hasValidMultiQuestions(): boolean {
    return MultiQuestionHelper.hasValidMultiQuestions(this);
  }

  sendMultiQuestions(): void {
    return MultiQuestionHelper.sendMultiQuestions(this);
  }

  processNextQueuedQuestion(): void {
    return MultiQuestionHelper.processNextQueuedQuestion(this);
  }

  onSearchSubmit(): void {
    return ChatSearchHelper.onSearchSubmit(this);
  }
  // #endregion

  // #region Orchestration & Status Messages
  fallbackMessage(): string {
    const clientName = this.selected_client || '';
    const studyName = this.selected_study || '';

    return exceptionMessage
      .replace(/{{client}}/g, clientName)
      .replace(/{{study}}/g, studyName);
  }

  configChanges(data: any, study: any) {
    return ChatHistoryHelper.configChanges(this, data, study);
  }

  Summary() {
    return this.inforRibbon?.nativeElement.textContent || '';
  }

  private getOrchestrationRequest(): Observable<any> {
    return PayloadBuilderHelper.getOrchestrationRequest(this);
  }

  private queueStatusMessage(message: string): void {
    return InsightsStreamingHelper.queueStatusMessage(this, message);
  }

  private processNextStatus(): void {
    return InsightsStreamingHelper.processNextStatus(this);
  }

  // #endregion

  // #region Table Output & Data Binding

  populateWeightDropdown(): void {
    DropdownHandlersHelper.populateWeightDropdown(this);
  }

  isSelected(SelectedValue: string, block?: any): boolean {
    const target = block || this;
    return target.selectedIndex === SelectedValue;
  }

  isSelectedSignificance(SelectedValue: string, block?: any): boolean {
    const target = block || this;
    return target.SignificanceVsReference === SelectedValue;
  }

  // Strip parent prefix from nested child values (e.g., "Audience|Gender|Male" -> "Gender|Male")
  private normalizeNestedChildFields(data: any[]): void {
    return DataTransformersHelper.normalizeNestedChildFields(this, data);
  }


  BindTableOutput(CrossTabOuputData: any, block: any = null) {
    return TableBuilderHelper.BindTableOutput(this, CrossTabOuputData, block);
  }

  replacePipe(input: string): string {
    return SelectionSummaryHelper.replacePipeForMcode(this, input);
  }

  onTableIconClick(block: any = null) {
    return DataSortHelper.onTableIconClick(this, block);
  }

  sortOutputData(block: any = null) {
    return DataSortHelper.sortOutputData(this, block);
  }

  toggleDataBlock(block: string, chatBlock?: any): void {
    ViewTogglerHelper.toggleDataBlock(this, block, chatBlock);
  }

  toggleSigBlock(blockName: string, chatBlock?: any): void {
    ViewTogglerHelper.toggleSigBlock(this, blockName, chatBlock);
  }
  createTable(isSampleSize: boolean, feature: string = Feature.CrosstabOutput, isIndexing: boolean, block: any = null) {
    return TableBuilderHelper.createTable(this, isSampleSize, feature, isIndexing, block);
  }

  usageDataLog(feature: string) {
    return ChatHistoryHelper.usageDataLog(this, feature);
  }

  AssignDataToPreviousDataObject(dataObject: DataObject) {
    return DataTransformersHelper.AssignDataToPreviousDataObject(this, dataObject);
  }

  private isWaveTimePeriod() {
    return DataTransformersHelper.isWaveTimePeriod(this);
  }

  clearOutputDataObject(block: any = null) {
    const target = block || this;
    target.OutputdataObject = {
      Column: [],
      Row: [],
    };
    target.FinalOutputdataObject = {
      Row: [],
      Column: [],
    };
  }

  getDistinctAggregaredMetrics(list: any[]) {
    return DataTransformersHelper.getDistinctAggregaredMetrics(list);
  }

  getDistinctENtryList(list: any[]) {
    return DataTransformersHelper.getDistinctENtryList(list);
  }
  // #endregion

  // #region Chart & Data Utilities

  // Utility Methods
  private tokenizeSearchText(searchText: string): string[]  {
    return ChatSearchHelper.tokenizeSearchText(this, searchText);
  }

  private matchesAllTokens(text: string, tokens: string[]): boolean {
    return DataTransformersHelper.matchesAllTokens(text, tokens);
  }

  private filterByVariableCode(dataArray: any[], variableCode: string, responseCode?: string): any[] {
    return DataTransformersHelper.filterByVariableCode(this.variableTabData, variableCode, responseCode);
  }

  private getDistinctValues<T, K extends keyof T>(list: T[], key: K): T[K][] {
    return DataTransformersHelper.getDistinctValues(list, key);
  }

  getDistinctLevel0Values(list: any[]) {
    return DataTransformersHelper.getDistinctLevel0Values(list);
  }

  getDistinctLevel1Values(list: any[]) {
    return DataTransformersHelper.getDistinctLevel1Values(list);
  }

  private sortByChildIndex(items: any[], referenceData: any[]): void {
    DataTransformersHelper.sortByChildIndex(items, referenceData);
  }

  private filterByPipeLevels(child: string): any[] {
    if (!this.variableTabData || this.variableTabData.length === 0) {
      this.variableTabData = this.crossTabService.getVarTabData || [];
    }
    return ChartUtilitiesHelper.filterByPipeLevels(child, this.variableTabData || []);
  }

  private resetChartStates(activeChart?: 'table' | 'line' | 'bar' | 'stack' | 'groupBar', block: any = null): void {
    return ChartUtilitiesHelper.resetChartStates(this, activeChart, block);
  }

  private applyChartStates(activeChart?: string, block: any = null): void {
    return ChartUtilitiesHelper.applyChartStates(this, activeChart, block);
  }

  private getShareNumber(item: CrossTabOutputItem): number | null {
    return ChartUtilitiesHelper.getShareNumber(item, false, 'Weight');
  }

  private neutralizeChartIds(block: any = null): void {
    return ChartUtilitiesHelper.neutralizeChartIds(this, block);
  }

  private calculatePercentageValue(shareNumber: number | null, item: CrossTabOutputItem): number | string {
    return ChartUtilitiesHelper.calculatePercentageValue(shareNumber, item);
  }

  private getChangeVsPY(item: CrossTabOutputItem): number | string {
    // Hardcoded to Answer base as per user request
    return ChartUtilitiesHelper.getChangeVsPY(item, false);
  }

  private getSignificanceString(item: CrossTabOutputItem): string {
    return ChartUtilitiesHelper.getSignificanceString(item, false);
  }

  private getSampleSizeString(item: CrossTabOutputItem): string {
    const s = ChartUtilitiesHelper.getSampleSizeString(item, false);
    return s === '0' ? (item.totalUnweightedBase?.toString() || '999') : s;
  }

  private getDistinctChildValuesFormatted(dataObject: checkboxSelectionWithParent[]): string[] {
    return ChartUtilitiesHelper.getDistinctChildValuesFormatted(dataObject);
  }

  private filterDataByCalculationType(data: checkboxSelectionWithParent[], type: 'multipunch' | 'respgrp' | 'mean'): checkboxSelectionWithParent[] {
    return ChartUtilitiesHelper.filterDataByCalculationType(data || [], type);
  }

  private initializeChartData(chart: LineChart | StackChart | GroupBarChart): void {
    ChartUtilitiesHelper.initializeChartData(chart);
  }

  private sortChartDataBySeriesName(chartData: any[], distinctRowChildValues: string[]): void {
    ChartUtilitiesHelper.sortChartDataBySeriesName(chartData, distinctRowChildValues);
  }

  private getDistinctPipeParts(rowValues: string[]): { first: string[], second: string[], third: string[], fourth: string[], fifth: string[] } {
    return ChartUtilitiesHelper.getDistinctPipeParts(rowValues);
  }

  private calculateStackFlag(parts: { first: string[], second: string[], third: string[], fourth: string[], fifth: string[] }): boolean {
    return ChartUtilitiesHelper.calculateStackFlag(parts);
  }

  private getDistinctColumnChildValues(outputData: any = null): string[] {
    return ChartUtilitiesHelper.getDistinctColumnChildValues((outputData || this.OutputdataObject) as any);
  }

  isDimensionPresent(list: any[]): boolean {
    return DataTransformersHelper.isDimensionPresent(list);
  }

  isLevel1TimePeriodPresent(list: any[]): boolean {
    return DataTransformersHelper.isLevel1TimePeriodPresent(list);
  }

  getLastLevelColumnNestingValues(block?: any) {
    return DataTransformersHelper.getLastLevelColumnNestingValues(this, block);
  }

  SelectionSummary() {
    return SelectionSummaryHelper.SelectionSummary(this);
  }

  BindSelectionSummary(
    RowValues: string[], RowNestingValues: string[], ColumnValues: string[], ColumnNestingValues: string[], FilterData: string[], isSavedSelection: boolean
  ) {
    return SelectionSummaryHelper.BindSelectionSummary(
      this, RowValues, RowNestingValues, ColumnValues, ColumnNestingValues, FilterData, isSavedSelection
    );
  }

  transformData(dataList: string[]): string[] {
    return SelectionSummaryHelper.transformData(dataList);
  }

  reverseTranspose() {
    return DataSortHelper.reverseTranspose(this);
  }

  private extractViewPayload(view: any, keys: string[]): any {
    return PayloadBuilderHelper.extractViewPayload(this, view, keys);
  }

  resolveEffectivePayloads(target: any): { effectiveCrosstabPayload: any; effectiveBrandPayload: any } {
    return PayloadBuilderHelper.resolveEffectivePayloads(this, target);
  }

  // #endregion

  // #region Ribbon Dropdowns & Export Actions

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  closeDropdown(): void {
    this.showDropdown = false;
  }

  viewOnline(): void {
    return ExportHandlerHelper.viewOnline(this);
  }

  exportToExcel(): void {
    return ExportHandlerHelper.exportToExcel(this);
  }

  async SampleSizeDropDownClick(SelectedValue: string, block: any = null) {
    return DropdownHandlersHelper.SampleSizeDropDownClick(this, SelectedValue, block);
  }

  processPayloadServiceResponse(res: any, block: any = null) {
    PayloadServiceHelper.processPayloadServiceResponse(this, res, block);
  }

  async confidenceDropDownClick(SelectedValue: string, block: any = null) {
    return DropdownHandlersHelper.confidenceDropDownClick(this, SelectedValue, block);
  }

  async callSignificancePayloadService(significanceType: string, payload: any = this.lastCrosstabPayload, block: any = null) {
    return PayloadServiceHelper.callSignificancePayloadService(this, significanceType, payload, block);
  }

  async callBrandAnalysisPayloadService(significanceType: string, payload: any = this.lastBrandAnalysisPayload, block: any = null) {
    return PayloadServiceHelper.callBrandAnalysisPayloadService(this, significanceType, payload, block);
  }

  referenceDropDownClickIndexSignificance(SelectedValue: string, block?: any) {
    DropdownHandlersHelper.referenceDropDownClickIndexSignificance(this, SelectedValue, block);
  }

  referenceDropDownClick(SelectedValue: string, block?: any) {
    DropdownHandlersHelper.referenceDropDownClick(this, SelectedValue, block);
  }

  togglePanel(SelectedDropDown: string, block?: any) {
    DropdownHandlersHelper.togglePanel(this, SelectedDropDown, block);
  }

  toggleActiveBlock(block: string, chatBlock?: any): void {
    DropdownHandlersHelper.toggleActiveBlock(this, block, chatBlock);
  }

  weightDropDownClick(SelectedValue: string, selectedWeight: string, chatBlock?: any) {
    DropdownHandlersHelper.weightDropDownClick(this, SelectedValue, selectedWeight, chatBlock);
  }

  handleExportClick(event?: Event, block: any = null) {
    if (event) event.stopPropagation();
    this.isExportspopup = false;
    ExportHandlerHelper.handleExportClick(this, block);
  }

  OnTransposeClick(block: any = null) {
    return TransposeHideNaHelper.OnTransposeClick(this, block);
  }

  OnHideNAClick(block: any = null) {
    return TransposeHideNaHelper.OnHideNAClick(this, block);
  }

  getCellValue(cell: any, block: any = null) {
    return TransposeHideNaHelper.getCellValue(this, cell, block);
  }

  getHideNAFilteredData(data: any[], isTransposed: boolean = false, block: any = null): any[] {
    return TransposeHideNaHelper.getHideNAFilteredData(this, data, isTransposed, block);
  }

  getHideNAFilteredByColumn(data: any[], block: any = null): any[] {
    return TransposeHideNaHelper.getHideNAFilteredByColumn(this, data, block);
  }

  getHideNAFilteredByRow(data: any[], block: any = null): any[] {
    return TransposeHideNaHelper.getHideNAFilteredByRow(this, data, block);
  }

  handlePPTExportClick(event?: Event, block: any = null) {
    if (event) event.stopPropagation();
    this.isExportspopup = false;
    ExportHandlerHelper.handlePPTExportClick(this, block);
  }

  fetchDiagnostic() {
    return ExportHandlerHelper.fetchDiagnostic(this);
  }

  displayDiagnosticData(data: DiagnosticData[]) {
    DiagnosticViewerHelper.displayDiagnosticData(data);
  }

  afterSubmit() {
    return AfterSubmitHelper.afterSubmit(this);
  }

  sortTrendTimeporiodData(metricData: any[]): any[] {
    return DataTransformersHelper.sortTrendTimeporiodData(this, metricData);
  }

  getMonthNumber(monthAbbr: any) {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec',];
    return months.indexOf(monthAbbr) + 1;
  }
  // #endregion

  // #region Insights
  OnInsigthsbuttonClick(isChatResponse = false, getSnapshotInsights?: any, getSnapshotInsightsTrend?: any, targetBlock?: any) {
    return InsightsApiHelper.OnInsigthsbuttonClick(this, isChatResponse, getSnapshotInsights, getSnapshotInsightsTrend, targetBlock);
  }

  async callInsightsAPI(
    mcode: number, markdown: string,
    query?: string, widget_details?: any,
    targetBlock?: any
  ): Promise<void> {
    return InsightsApiHelper.callInsightsAPI(this, mcode, markdown, query, widget_details, targetBlock);
  }

  private processInsightsChunkRealTime(newChunk: string, fullResponse: string): void {
    return InsightsStreamingHelper.processInsightsChunkRealTime(this, newChunk, fullResponse);
  }

  private processInsightsChunkFinal(fullInsightsResponse: string): void {
    return InsightsStreamingHelper.processInsightsChunkFinal(this, fullInsightsResponse);
  }

  formatInsightsIntoPoints(text: string): string[] {
    return InsightsStreamingHelper.formatInsightsIntoPoints(this, text);
  }

  private extractTextFromStreamEvent(event: any): string {
    return InsightsStreamingHelper.extractTextFromStreamEvent(this, event);
  }

  private cleanStreamingArtifacts(text: string): string {
    return InsightsStreamingHelper.cleanStreamingArtifacts(this, text);
  }

  updateInsightsPopupRealTime(): void {
    return InsightsStreamingHelper.updateInsightsPopupRealTime(this);
  }

  updateInsightsPopupFinal(): void {
    return InsightsStreamingHelper.updateInsightsPopupFinal(this);
  }

  private scrollInsightsDownSmooth(): void {
    InsightsStreamingHelper.scrollInsightsDownSmooth(this);
  }

  private scrollInsightsToTopSmooth(): void {
    return InsightsStreamingHelper.scrollInsightsToTopSmooth(this);
  }

  private stopInsightsAutoScroll(): void {
    InsightsStreamingHelper.stopInsightsAutoScroll(this);
  }

  private updateMarkdownTextRealTime(qnaResponse: string): void {
    InsightsStreamingHelper.updateMarkdownTextRealTime(this, qnaResponse);
  }

  private cleanQnaTextForParaOnly(text: string): string {
    return InsightsStreamingHelper.cleanQnaTextForParaOnly(this, text);
  }

  private checkStreamCompletion(insightsCompleted: boolean, qnaCompleted: boolean, insightsResponse: string, qnaResponse: string, mcode: number, targetBlock: any): void {
    return InsightsStreamingHelper.checkStreamCompletion(this, insightsCompleted, qnaCompleted, insightsResponse, qnaResponse, mcode, targetBlock);
  }

  buildSnapshotMarkdownForAPI(): string {
    return InsightsGeneratorHelper.buildSnapshotMarkdownForAPI(
      this.OutputData, this.isTotalBase, this.selectedWeight,
      Number(this.insufficientBase || 0)
    );
  }

  buildCrosstabMarkdownForAPI(): string {
    return InsightsGeneratorHelper.buildCrosstabMarkdownForAPI(
      this.CrossTabOutPutData, this.isTotalBase,
      this.selectedWeight, Number(this.insufficientBase || 0)
    );
  }

  // #endregion

  // #region Message Send Pipeline

  private resetSessionState(): void {
    SessionStateHelper.resetSessionState(this);
  }

  sendInputMessage(ChatResponse?: any, isChatResponse: boolean = false, targetBlock: any = null) {
    return SendInputMessageHelper.sendInputMessage(this, ChatResponse, isChatResponse, targetBlock);
  }

  private continueProcessingMessage(ChatResponse: any, isChatResponse: boolean, targetBlock: any = null) {
    return OrchestrationHelper.continueProcessingMessage(this, ChatResponse, isChatResponse, targetBlock);
  }

  AssignDataToDataObject(datamodel: any) {
    this.isTotalBaseFromPrompt = datamodel.isTotalBase;
  }

  // #endregion

  // #region Ribbon & Chart-Click Wrappers

  setSampleSizeChange(event: boolean) {
    setTimeout(() => {
      this.isSampleSize = event;
      this.cdr.markForCheck();
    });
  }

  onTrendToggle(block: any) {
    return ViewTogglerHelper.onTrendToggle(this, block);
  }

  onChartIconMouseOver(tooltipType: 'stack' | 'line' | 'bar', event: MouseEvent, block: any = null): void {
    return ChartTooltipsHelper.onChartIconMouseOver(this, tooltipType, event, block);
  }

  onChartIconMouseLeave(tooltipType: 'stack' | 'line' | 'bar', block: any = null): void {
    return ChartTooltipsHelper.onChartIconMouseLeave(this, tooltipType, block);
  }

  private updateChartDisablingFlags(block: any = null): void {
    return ChartTooltipsHelper.updateChartDisablingFlags(this, block);
  }

  private computeStudyRibbonFlags(): { confidenceLevels: any[]; transposeAvailable: boolean } {
    return RibbonConfigHelper.computeStudyRibbonFlags(this);
  }

  LoadRibbonData(receivedData: any) {
    return RibbonConfigHelper.LoadRibbonData(this, receivedData);
  }

  manipulateIndexData(block: any = null) {
    return DataSortHelper.manipulateIndexData(this, block);
  }

  onVerticalStackBarChartIconClick(chartType: string, block: any = null, distinctColChildValues?: string[]) {
    return ChartClickHelper.onVerticalStackBarChartIconClick(this, chartType, block, distinctColChildValues);
  }

  onTrendChartIconClick(block: any = null) {
    return ChartClickHelper.onTrendChartIconClick(this, block);
  }

  getDistinctChildValues(data: checkboxSelectionWithParent[]): string[] {
    return ChartUtilitiesHelper.getDistinctChildValues(data || []);
  }

  onVerticalBarChartIconClick(chartType: string, block: any = null, distinctColChildValues?: string[]) {
    return ChartClickHelper.onVerticalBarChartIconClick(this, chartType, block, distinctColChildValues);
  }

  onGroupVerticalChartClick(chartType: string, block: any = null, distinctColChildValues?: string[]) {
    return ChartClickHelper.onGroupVerticalChartClick(this, chartType, block, distinctColChildValues);
  }

  VerticalChartClick(chartType: string, block: any = null) {
    return ChartClickHelper.VerticalChartClick(this, chartType, block);
  }

  getDistinctValuesByPipeCount(data: string[], pipeCount: number): string[] {
    return ChartUtilitiesHelper.getDistinctValuesByPipeCount(data, pipeCount);
  }

  handleOutputdataObjectOnTransposeSelect(block: any = null) {
    return DataSortHelper.handleOutputdataObjectOnTransposeSelect(this, block);
  }

  calcThershold(type: string): string {
    return ChartUtilitiesHelper.calcThershold(this, type);
  }

  openInsights(isChatResponse = false, getInsights?: any) {
    return OpenInsightsHelper.openInsights(this, isChatResponse, getInsights);
  }

  SelectionsummaryCollapseExpand(text: any, block: any = null) {
    return SelectionSummaryHelper.SelectionsummaryCollapseExpand(this, text, block);
  }

  OnCloseClick() {
    return SelectionSummaryHelper.OnCloseClick(this);
  }

  Exportspopup(event?: Event, block?: any) {
    if (event) event.stopPropagation();
    if (block) block.isExportspopup = !block.isExportspopup;
    else this.isExportspopup = !this.isExportspopup;
  }
  // #endregion

  // #region Chat List & Persistence

  GenerateSelectionSummary(customSummary?: string) {
    return SelectionSummaryHelper.GenerateSelectionSummary(this, customSummary);
  }

  getChatListData() {
    return ChatHistorySidebarHelper.getChatListData(this);
  }

  loadLatestChatIfExists() {
    return ChatHistorySidebarHelper.loadLatestChatIfExists(this);
  }

  onChatSearch(): void  {
    return ChatSearchHelper.onChatSearch(this);
  }

  toggleNestedSearch(): void  {
    return ChatSearchHelper.toggleNestedSearch(this);
  }

  saveChatList(title: string, chatResponse?: { [key: string]: string }[], questionAnswerMarkdown?: string, messageId?: string) {
    return ChatHistoryHelper.saveChatList(this, title, chatResponse, questionAnswerMarkdown, messageId);
  }

  saveChatHistory(
    chatId: string, questionAnswer?: { [key: string]: string }[], question?: string, messageIndex?: number,
    questionAnswerMarkdown?: string, isLiked?: boolean | null, likeReason?: string, dislikeReason?: string, messageId?: string
  ) {
    return ChatHistoryHelper.saveChatHistory(this, chatId, questionAnswer, question, messageIndex, questionAnswerMarkdown, isLiked, likeReason, dislikeReason, messageId);
  }

  prepareDiagnosticPayload(): any {
    return PayloadBuilderHelper.prepareDiagnosticPayload(this);
  }

  async getChatResponse(messageId: string | undefined, showSingleMessageOnly: boolean = false, autoScroll: boolean = true) {
    return GetChatResponseHelper.getChatResponse(this, messageId, showSingleMessageOnly, autoScroll);
  }

  loadAnswersIncrementally(chat: any, messageId: string): void {
    return ChatAnswerLoadingHelper.loadAnswersIncrementally(this, chat, messageId);
  }

  lazyFetchBlockAnswer(block: any): void {
    return ChatAnswerLoadingHelper.lazyFetchBlockAnswer(this, block);
  }

  private populateBlockInsights(block: any, parsed: any): void {
    return ChatAnswerLoadingHelper.populateBlockInsights(this, block, parsed);
  }

  private resolvePendingMessage(messageId: string): void {
    return ChatAnswerLoadingHelper.resolvePendingMessage(this, messageId);
  }

  onChatScroll(event: Event): void {
    UIEffectsHelper.onChatScroll(this, event);
  }

  // #endregion

  // #region Per-Block Template Getters

  // Helper getters to resolve data per block in the template
  getOutputData(block: any): any {
    return DataTransformersHelper.getOutputData(this, block);
  }

  getMcode(block: any): number {
    if (block.messageId === this.selectedMessageId) {
      return this.mcode;
    }
    return block.mcode || 0;
  }

  isSnapshotVisible(block: any): boolean {
    return ViewTogglerHelper.isSnapshotVisible(this, block);
  }

  isActionsVisible(block: any): boolean {
    if (block.messageId === this.selectedMessageId) {
      return (this.isCreateBtnClick || this.showSnapshot) && !this.failedCase;
    }
    return !!(block.analyticalData || block.fullAnswer) && !block.failedCase;
  }

  isInsightVisible(block: any): boolean {
    return ViewTogglerHelper.isInsightVisible(this, block);
  }

  getInsightsForBlock(block: any): string[] {
    return CopyClipboardHelper.getInsightsForBlock(this, block);
  }

  copyResponseToClipboard(block: any) {
    return CopyClipboardHelper.copyResponseToClipboard(this, block);
  }

  async copyFullResponse(block: any) {
    return CopyClipboardHelper.copyFullResponse(this, block);
  }

  async handleCopyChartClick(event: Event, block: any, popupCdr?: ChangeDetectorRef) {
    return CopyClipboardHelper.handleCopyChartClick(this, event, block, popupCdr);
  }

  copyInsightsToClipboard(block: any) {
    return CopyClipboardHelper.copyInsightsToClipboard(this, block);
  }

  getCustomData(block: any): any {
    return ChatHistoryHelper.getCustomData(this, block);
  }

  buildHistoricalWidgetData(data: any, analytical?: any): any {
    return ChatHistoryHelper.buildHistoricalWidgetData(this, data, analytical);
  }

  buildHistoricalTableData(data: any): any {
    return TableBuilderHelper.buildHistoricalTableData(this, data);
  }

  getSnapshotConfig(block: any): any {
    const config = (block.messageId === this.selectedMessageId && this.selectedMessageId !== null)
      ? (this.snapshotConfiguration || {})
      : (block.fullAnswer?.getSnapshotConfiguration || block.analyticalData?.getSnapshotConfiguration || {});
    return config || {};
  }

  isTableVisible(block: any): boolean {
    return TableBuilderHelper.isTableVisible(this, block);
  }

  isChartVisible(block: any): boolean {
    return UIEffectsHelper.isChartVisible(this, block);
  }

  isChartType(block: any, chartType: string): boolean {
    return ChartUtilitiesHelper.isChartType(this, block, chartType);
  }

  // #endregion

  // #region Chat Actions (Star, Delete, Feedback)

  toggleStarred(chatBlock?: any): void {
    ViewTogglerHelper.toggleStarred(this, chatBlock);
  }

  initiateDeleteChat(event: Event, chat: any): void {
    event.stopPropagation();
    this.chatToDelete = chat;
  }

  cancelDeleteChat(): void {
    if (this.isDeletingChat) return;
    this.chatToDelete = null;
  }

  confirmDeleteChatSession(): void {
    ChatHistoryHelper.confirmDeleteChatSession(this);
  }

  onDeleteClick(chat: any) {
    return ChatHistoryHelper.onDeleteClick(this, chat);
  }

  cancelDelete(chat: any) {
    chat.showDeleteConfirm = false;
  }

  confirmDelete(chat: any) {
    ChatHistoryHelper.confirmDelete(this, chat);
  }

  onLikeClick(chat: { question: string; response: string; messageId?: string; isLiked?: boolean | null; feedbackSaved?: boolean; showLikeInput?: boolean; likeReason?: string; showDislikeInput?: boolean; dislikeReason?: string }) {
    return FeedbackHelper.onLikeClick(this, chat);
  }

  onDislikeClick(chat: { question: string; response: string; messageId?: string; isLiked?: boolean | null; feedbackSaved?: boolean; showLikeInput?: boolean; likeReason?: string; showDislikeInput?: boolean; dislikeReason?: string }) {
    return FeedbackHelper.onDislikeClick(this, chat);
  }

  submitLikeFeedback(chat: { question: string; response: string; messageId?: string; isLiked?: boolean | null; feedbackSaved?: boolean; showLikeInput?: boolean; likeReason?: string; showDislikeInput?: boolean; dislikeReason?: string }) {
    return FeedbackHelper.submitLikeFeedback(this, chat);
  }

  cancelLikeFeedback(chat: { question: string; response: string; messageId?: string; isLiked?: boolean | null; feedbackSaved?: boolean; showLikeInput?: boolean; likeReason?: string }) {
    return FeedbackHelper.cancelLikeFeedback(this, chat);
  }

  submitDislikeFeedback(chat: { question: string; response: string; messageId?: string; isLiked?: boolean | null; feedbackSaved?: boolean; showLikeInput?: boolean; likeReason?: string; showDislikeInput?: boolean; dislikeReason?: string }) {
    return FeedbackHelper.submitDislikeFeedback(this, chat);
  }

  cancelDislikeFeedback(chat: { question: string; response: string; messageId?: string; isLiked?: boolean | null; feedbackSaved?: boolean; showDislikeInput?: boolean; dislikeReason?: string }) {
    return FeedbackHelper.cancelDislikeFeedback(this, chat);
  }

  parseChatResponse(ChatResponse: any): {
    AgentPayload?: any;
    GetMcode?: any;
    getSnapshotConfiguration?: any;
    GetCustomSnapshotWidget?: any;
    GetQuery?: any;
    getSnapshotOutput?: any;
    getSnapshotLoadData?: any;
    getConfiguration?: any;
    getSnapshotInsights?: any;
    getSnapshotInsightsTrend?: any;
    getInsights?: any;
    getQnA?: any;
    trendline_response?: any;
    response_payload_trendline?: any;
    customization_Properties_trendline?: any;
    response_payload?: any;
    member_responses?: any;
  } {
    return ChatHistoryHelper.parseChatResponse(ChatResponse);
  }

  pushChatResponseEntry(key: string, value: any) {
    const entry = ChatHistoryHelper.formatChatResponseEntry(key, value);
    this.chatResponse.push(entry);
  }

  // #endregion

  // #region Exports, Toasts & UI Helpers

  goToLandingPage(): void {
    this.router.navigateByUrl('/landing');
  }

  triggerTabEnabledAnimation() {
    return UIEffectsHelper.triggerTabEnabledAnimation(this);
  }

  excelexportClicked() {
    ExportHandlerHelper.excelexportClicked(this);
  }

  handleExcelExportClick() {
    ExportHandlerHelper.handleExcelExportClick(this);
  }

  editablePPTClicked(isTredendDataRequired: boolean = false) {
    ExportHandlerHelper.editablePPTClicked(this, isTredendDataRequired);
  }

  onNewChatClick(): void {
    return ChatHistoryHelper.onNewChatClick(this);
  }

  showToast(message: string, type: 'success' | 'error' | 'info' = 'success') {
    this.toastMessage = message;
    this.toastType = type;
    this.showToastNotification = true;
    this.toastRemainingTime = 3000;
    this.isToastHovered = false;
    this.startToastTimer();
  }

  startToastTimer() {
    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
    }
    this.toastStartTime = Date.now();
    this.toastTimeoutId = setTimeout(() => {
      this.showToastNotification = false;
    }, this.toastRemainingTime);
  }

  onToastMouseEnter() {
    this.isToastHovered = true;
    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
      this.toastRemainingTime -= (Date.now() - this.toastStartTime);
    }
  }

  onToastMouseLeave() {
    this.isToastHovered = false;
    if (this.toastRemainingTime > 0) {
      this.startToastTimer();
    } else {
      this.showToastNotification = false;
    }
  }

  sendKeyQuestion(question: string): void {
    this.searchText = question;
    this.navigateToChat();
    this.onSendMessage();
  }

  getFirstTwoInsights(): any[] {
    if (!this.insightsVal || !Array.isArray(this.insightsVal)) {
      return [];
    }
    return this.insightsVal.slice(0, 2);
  }

  getFirstTwoInsightsAsPlainText(): string {
    return InsightsGeneratorHelper.getFirstTwoInsightsAsPlainText(this);
  }

  getCurrentQnAResponse(block?: any): string {
    return ChatHistoryHelper.getCurrentQnAResponse(this, block);
  }

  isFailedCase(block: any): boolean {
    if (block && block.messageId === this.selectedMessageId) {
      return this.failedCase;
    }
    return block ? !!block.failedCase : this.failedCase;
  }

  setFailedCaseState(value: boolean, targetBlock?: any) {
    this.failedCase = value;
    const target = targetBlock || this.chatPreviewBlocks.find(b => b.messageId === this.selectedMessageId);
    if (target) {
      target.failedCase = value;
    }
  }

  getPostStreamMessage(): string {
    return ChatHistoryHelper.getPostStreamMessage(this);
  }

  updateQuestionAnswerForSelectedMessage(questionAnswerText: string): void {
    return ChatHistoryHelper.updateQuestionAnswerForSelectedMessage(this, questionAnswerText);
  }

  startAiLoader(): void {
    this.arktabaccess = true;
  }

  stopAiLoader(): void {
    this.arktabaccess = false;
  }

  getFormattedCustomProperties(props: any, chartType: string): any {
    return ChartUtilitiesHelper.getFormattedCustomProperties(props, chartType);
  }

  scrollInsights(direction: 'up' | 'down', block: any, container: HTMLElement): void {
    return UIEffectsHelper.scrollInsights(this, direction, block, container);
  }

  checkInsightsOverflow(block: any, container: HTMLElement): void {
    return InsightsStreamingHelper.checkInsightsOverflow(this, block, container);
  }

  // #endregion

  // #region Multi-View & Step 2 Rendering

  // --- Multi-View Logic ---

  constructSpecialized(code: number, res: any, rawData: any = this.orchestrationRawData) {
    return PayloadBuilderHelper.constructSpecialized(this, code, res, rawData);
  }

  private fetchInsightsAsObservable(mcode: number, opData: any, msgId: string, orchData?: any): Observable<any> {
    return InsightsApiHelper.fetchInsightsAsObservable(this, mcode, opData, msgId, orchData);
  }
  redirectToModule(block: any, moduleName: 'Crosstab' | 'BrandAnalysis'): void {
    return RedirectHelper.redirectToModule(this, block, moduleName);
  }

  redirectToSnapshot(block: any): void {
    return RedirectHelper.redirectToSnapshot(this, block);
  }

  async switchAnalysisView(view: any, block?: any) {
    return await AnalysisViewSwitcherHelper.switchAnalysisView(this, view, block);
  }

  identifyAvailableViews(rawData: any, parsedMemberResponses?: any) {
    return OrchestrationHelper.identifyAvailableViews(rawData, parsedMemberResponses);
  }

  processStep2Response(opData: any, promptResult: any, preventScroll: boolean = false, targetBlock?: any) {
    Step2ProcessorHelper.processStep2Response(this, opData, promptResult, preventScroll, targetBlock);
  }

  syncDataObjectFromPrompt(res: any) {
    SessionStateHelper.syncDataObjectFromPrompt(this, res);
  }

  // #endregion

  // #region Chat Search

  // --- Chat Search Logic ---
  setupSearchDebounce()  {
    return ChatSearchHelper.setupSearchDebounce(this);
  }

  toggleChatSearch()  {
    return ChatSearchHelper.toggleChatSearch(this);
  }

  closeChatSearch()  {
    return ChatSearchHelper.closeChatSearch(this);
  }

  onSearchInput(event: any)  {
    return ChatSearchHelper.onSearchInput(this, event);
  }

  onSearchKeydown(event: KeyboardEvent)  {
    return ChatSearchHelper.onSearchKeydown(this, event);
  }

  scrollSearchSelectionIntoView()  {
    return ChatSearchHelper.scrollSearchSelectionIntoView(this);
  }

  performSearch(query: string)  {
    return ChatSearchHelper.performSearch(this, query);
  }

  selectSearchResult(result: any)  {
    return ChatSearchHelper.selectSearchResult(this, result);
  }

  loadChatDetails(chatId: string, targetMessageId?: string, matchModule?: number) {
    return ChatHistoryHelper.loadChatDetails(this, chatId, targetMessageId, matchModule);
  }

  playNotificationSound() {
    UIEffectsHelper.playNotificationSound(this);
  }
  // #endregion
}